import { useEffect, useState, type SyntheticEvent } from 'react';
import { authApi } from '../../lib/api/endpoints.js';
import { ApiError } from '../../lib/api/client.js';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { PASSWORD_MIN_LENGTH, type AuthOptions } from '@indusketch/shared';
import { userMessage } from '../../lib/errors.js';
import { useI18n } from '../../i18n/i18n.js';
import { LanguageSwitcher } from '../../i18n/LanguageSwitcher.js';
import { useAuthStore } from './authStore.js';

const ACCESS_CODE_KEY = 'indusketch.accessCode';

function loadAccessCode(): string {
  try {
    return localStorage.getItem(ACCESS_CODE_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveAccessCode(code: string) {
  try {
    if (code) localStorage.setItem(ACCESS_CODE_KEY, code);
    else localStorage.removeItem(ACCESS_CODE_KEY);
  } catch {
    // Private mode: the code has to be entered again next visit.
  }
}

/** Kept off-screen instead of `display: none`, which some bots recognise and skip. */
const honeypotStyle = {
  position: 'absolute',
  left: '-10000px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
} as const;

export function LoginPage() {
  const { m } = useI18n();
  const status = useAuthStore((s) => s.status);
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Null while loading: neither the code prompt nor the forms flash up before the server answered.
  // Registration stays hidden until confirmed, so admin-only setups never show it.
  const [options, setOptions] = useState<AuthOptions | null>(null);
  const [accessCode, setAccessCode] = useState(loadAccessCode);
  const [codeInput, setCodeInput] = useState('');
  const [registered, setRegistered] = useState(false);
  useEffect(() => {
    authApi
      .options()
      .then(setOptions)
      .catch(() =>
        setOptions({
          allowRegistration: false,
          requiresApproval: false,
          accessCodeRequired: false,
        }),
      );
  }, []);

  const from = (location.state as { from?: string } | null)?.from ?? '/projects';
  if (status === 'authenticated') return <Navigate to={from} replace />;

  const locked = options?.accessCodeRequired === true && !accessCode;

  const onUnlock = async (e: SyntheticEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const code = codeInput.trim();
    try {
      await authApi.checkAccessCode(code);
      saveAccessCode(code);
      setAccessCode(code);
      setCodeInput('');
    } catch (err) {
      setError(userMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') await login({ email, password });
      else if (
        (await register({
          email,
          password,
          displayName,
          ...(accessCode ? { accessCode } : {}),
          ...(website ? { website } : {}),
        })) === 'pending'
      ) {
        setRegistered(true);
        setMode('login');
        setPassword('');
        return;
      }
      await navigate(from, { replace: true });
    } catch (err) {
      // A stored code that has since been changed brings the code prompt back.
      if (mode === 'register' && err instanceof ApiError && err.status === 403) {
        saveAccessCode('');
        setAccessCode('');
      }
      setError(userMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const head = (
    <>
      <div className="auth-head">
        <h1>InduSketch</h1>
        <LanguageSwitcher compact />
      </div>
      <p className="muted">{m.app.tagline}</p>
    </>
  );

  if (!options) {
    return (
      <div className="auth-page">
        <div className="card auth-card">{head}</div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="auth-page">
        <form
          className="card auth-card"
          onSubmit={(e) => void onUnlock(e)}
          data-testid="access-code-form"
        >
          {head}
          <label className="field">
            <span>{m.auth.accessCode}</span>
            <input
              className="input"
              type="password"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              required
              autoFocus
              autoComplete="off"
            />
          </label>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
            {m.auth.accessCodeContinue}
          </button>
          <p className="muted">{m.auth.accessCodeHint}</p>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <form className="card auth-card" onSubmit={(e) => void onSubmit(e)}>
        {head}
        {registered && (
          <p className="notice" role="status" data-testid="registration-pending">
            {m.auth.registrationPending}
          </p>
        )}
        {mode === 'register' && options.requiresApproval && (
          <p className="muted">{m.auth.approvalNotice}</p>
        )}
        {mode === 'register' && (
          <label className="field">
            <span>{m.common.name}</span>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              autoComplete="name"
            />
          </label>
        )}
        {mode === 'register' && (
          <div style={honeypotStyle} aria-hidden="true">
            <label>
              Website
              <input
                name="website"
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </label>
          </div>
        )}
        <label className="field">
          <span>{m.common.email}</span>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </label>
        <label className="field">
          <span>{m.common.password}</span>
          <input
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={mode === 'register' ? PASSWORD_MIN_LENGTH : 1}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {mode === 'login' ? m.auth.login : m.auth.createAccount}
        </button>
        {options.allowRegistration ? (
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError(null);
            }}
          >
            {mode === 'login' ? m.auth.newAccount : m.auth.haveAccount}
          </button>
        ) : (
          <p className="muted" data-testid="registration-closed">
            {m.auth.registrationClosed}
          </p>
        )}
      </form>
    </div>
  );
}
